/**
 * Listings hub page (`/listings/rent`, `/listings/buy`) — the map/list browse
 * surface.
 *
 * One component for both modes: they differ only in the transaction mode their
 * loader requested, and that already rides in the loader data. A second copy
 * is two places to forget the screen-reader heading (the reason the Ahrefs
 * wave-4 audit found these hubs shipping without an `<h1>` in all three
 * locales in the first place).
 *
 * `boundedPage` because `chrome: canvas` bounds `main` and clips it, and the
 * split of that box is the PAGE's to declare (#6915 / N2). The hub is the easy
 * member of the kind — its only flow content is a screen-reader heading with no
 * box at all — which is exactly why it was the one canvas every spec visited
 * and the facets were the ones that shipped broken.
 */
import { SeoHead } from '@/shared/components/SeoHead';
import { routes, urlModeFor } from '@letsgethome/routes';
import { boundedPage } from '@letsgethome/ui/bounded-page';
import { useLoaderData } from 'react-router';

import { ListingsClient } from '../components/ListingsClient';
import { useListingsPageHref } from '../components/ListingsPager';
import type { loader } from './rent.loader';

export const Component = boundedPage(function ListingsHub() {
  const {
    listings,
    totalCount,
    page,
    perPage,
    hasMore,
    standing,
    mapPosition,
    seo,
    filterSet,
    mode,
  } = useLoaderData<typeof loader>();
  // The hub's own base route; the current filters and map viewport ride along
  // (there is no sort — the price-ordering control and its URL param were
  // retired by #6843, and the wire enum behind them by #6927).
  const hrefForPage = useListingsPageHref(routes.listings.browse(urlModeFor(mode)));

  return {
    head: <SeoHead {...seo} />,
    // The hub is a full-screen map/list UI with no visible page heading — the
    // h1 is screen-reader-only, mirroring the SEO title so the page's one
    // heading and its <title> cannot diverge.
    flow: <h1 className="sr-only">{seo.title}</h1>,
    fill: (
      <ListingsClient
        surface="canvas"
        initialListings={listings}
        initialMeta={{ totalCount, page, perPage, hasMore, standing }}
        initialMapPosition={mapPosition}
        mode={mode}
        filters={filterSet}
        hrefForPage={hrefForPage}
      />
    ),
  };
});
